import React from 'react';
import type { IMessage } from '@/types/admin/messages';
import { isMessageSentByUser, formatSenderName } from '@/utils/residents/messages/messageUtils';
import { BaseMessageBubble } from '@/components/common/chat';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface MessageBubbleProps {
  message: IMessage;
  authUserId?: number;
}

/**
 * Message bubble component for property chat.
 * Wraps the BaseMessageBubble with property chat-specific theming.
 */
export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, authUserId }) => {
  const { semantic, components, colors } = useThemeColors();
  const sentByYou = isMessageSentByUser(message.sender?.id, authUserId, message.isAdmin);
  const senderName = formatSenderName(message, sentByYou, 'desktop');

  // Property chat uses fully theme-aware colors
  const theme = {
    sentBubbleColor: components.chat.sentBubble,
    receivedBubbleColor: components.chat.receivedBubble,
    sentTextColor: components.button.primaryText,
    receivedTextColor: colors.text.primary,
    senderNameColor: semantic.icon.inactive,
    timestampColor: components.chat.timestamp,
    accentColor: semantic.interactive.primary,
    // No border for admin messages
    receivedBubbleBorderColor: message.isAdmin ? undefined : semantic.border.secondary,
  };

  return (
    <BaseMessageBubble
      text={message.content}
      timestamp={message.createdAt}
      isSent={sentByYou}
      senderName={senderName}
      theme={theme}
      showSenderName={message.isAdmin}
      senderNameAtBottom={!message.isAdmin}
    />
  );
};

export default MessageBubble;
